import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/lib/auth/AuthContext";
import { isSupabaseEnabled } from "@/lib/supabase";
import { useI18n } from "@/lib/i18n/useI18n";

// ─── LogoMark: intenta cargar /logo.png, fallback al SVG ─────────────────────

function LogoMark() {
  return (
    <img
      src="/LogoVP.png?v=siftrip-20260807"
      alt=""
      aria-hidden
      className="shrink-0"
      style={{
        width:        "30px",
        height:       "30px",
        borderRadius: "8px",
        objectFit:    "cover",
      }}
    />
  );
}

// ─── User avatar chip ─────────────────────────────────────────────────────────

function UserChip() {
  const { user, signOut } = useAuth();
  const { t } = useI18n();
  const [open, setOpen] = useState(false);

  if (!user) return null;

  // Derive initials from email or display name
  const name    = user.user_metadata?.full_name as string | undefined;
  const avatar  = user.user_metadata?.avatar_url as string | undefined;
  const initials = name
    ? name.split(" ").map((w: string) => w[0]).join("").slice(0, 2).toUpperCase()
    : user.email?.slice(0, 2).toUpperCase() ?? "?";

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 rounded-full pl-1 pr-3 py-1 transition-colors hover:bg-accent"
        aria-label={t("nav.userMenuAria")}
      >
        {/* Avatar */}
        {avatar ? (
          <img
            src={avatar}
            alt={name ?? user.email ?? ""}
            className="w-7 h-7 rounded-full object-cover ring-1"
            style={{ boxShadow: "0 0 0 1px oklch(0.298 0.058 167 / 0.30)" }}
          />
        ) : (
          <span
            className="w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold text-white"
            style={{ background: "oklch(0.298 0.058 167)" }}
          >
            {initials}
          </span>
        )}
        {/* Name or email (truncated) */}
        <span className="text-sm font-medium text-foreground max-w-[120px] truncate">
          {name ?? user.email}
        </span>
        {/* Caret */}
        <svg
          width="12" height="12" viewBox="0 0 12 12" fill="none"
          className={`transition-transform duration-150 ${open ? "rotate-180" : ""}`}
          style={{ color: "oklch(0.545 0.020 60 / 0.55)" }}
        >
          <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {/* Dropdown */}
      <AnimatePresence>
        {open && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 z-40"
              onClick={() => setOpen(false)}
              aria-hidden
            />
            <motion.div
              initial={{ opacity: 0, y: -6, scale: 0.97 }}
              animate={{ opacity: 1, y: 0,  scale: 1 }}
              exit={  { opacity: 0, y: -6, scale: 0.97 }}
              transition={{ duration: 0.15 }}
              className="absolute right-0 top-full mt-2 z-50 min-w-[180px] rounded-xl border border-border bg-background shadow-lg py-1 overflow-hidden"
            >
              {/* Admin Supabase JWKS Auth + Profile Noise Cleanup V1 — "Mi
                  perfil" removed from the public menu. It pointed at
                  /dashboard, which reads public.traveler_profiles/
                  journey_history — tables that don't exist in production
                  (see lib/auth/profileSync.ts's PROFILE_SYNC_ENABLED).
                  There is no ready profile product behind this launch;
                  re-add once one exists. */}
              <button
                onClick={() => { setOpen(false); signOut(); }}
                className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-muted-foreground hover:text-foreground hover:bg-accent transition-colors text-left"
              >
                <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 1.5h5a.5.5 0 010 1H3.5v10h4.5a.5.5 0 010 1H3a.5.5 0 01-.5-.5v-11A.5.5 0 013 1.5zm7.354 3.354a.5.5 0 00-.708-.708L7.5 6.293l-.146-.147a.5.5 0 00-.708.708l.5.5a.5.5 0 00.708 0l2.5-2.5zM10.5 7.5a.5.5 0 00-1 0v3a.5.5 0 001 0v-3z" fill="currentColor"/>
                </svg>
                {t("nav.signOut")}
              </button>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Login button ─────────────────────────────────────────────────────────────

function LoginButton({ compact = false }: { compact?: boolean }) {
  const { signInWithGoogle } = useAuth();
  const { t } = useI18n();

  return (
    <button
      onClick={signInWithGoogle}
      className="flex items-center gap-2 rounded-full border border-border bg-background px-3 py-1.5 text-sm font-medium text-foreground transition-all hover:bg-accent hover:shadow-sm"
    >
      {/* Google "G" icon */}
      <svg width="16" height="16" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
        <path d="M15.68 8.18c0-.57-.05-1.12-.14-1.64H8v3.1h4.28a3.65 3.65 0 01-1.58 2.4v2h2.56c1.5-1.38 2.42-3.4 2.42-5.86z" fill="#4285F4"/>
        <path d="M8 16c2.16 0 3.97-.71 5.3-1.94l-2.56-2a5.02 5.02 0 01-2.74.76 5 5 0 01-4.7-3.46H.66v2.08A8 8 0 008 16z" fill="#34A853"/>
        <path d="M3.3 9.36A5.03 5.03 0 013.04 8c0-.47.08-.93.26-1.36V4.56H.66A8 8 0 000 8c0 1.29.31 2.51.66 3.44l2.64-2.08z" fill="#FBBC05"/>
        <path d="M8 3.18c1.22 0 2.3.42 3.16 1.24l2.36-2.36C12 .79 10.16 0 8 0A8 8 0 00.66 4.56l2.64 2.08A5 5 0 018 3.18z" fill="#EA4335"/>
      </svg>
      {compact ? t("nav.signInShort") : t("nav.continueWithGoogle")}
    </button>
  );
}

// ─── Main Navbar ──────────────────────────────────────────────────────────────

export function Navbar() {
  const { user, loading } = useAuth();
  const { t } = useI18n();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <nav
      className="fixed top-0 left-0 right-0 z-50 bg-background/92 backdrop-blur-xl border-b border-border"
      style={{ boxShadow: "0 1px 0 oklch(0.298 0.058 167 / 0.06), 0 2px 12px oklch(0.40 0.012 50 / 0.06)" }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">

        {/* ── Wordmark ──────────────────────────────────────────────────── */}
        <Link to="/" className="flex items-center gap-2 group">
          {/* Intenta cargar el logo real primero; si falla muestra el SVG */}
          <LogoMark />
          <span
            className="select-none transition-opacity group-hover:opacity-90 font-sans"
            style={{
              fontSize: "17px",
              fontWeight: 400,
              letterSpacing: "0.04em",
              color: "#1B2E72",
            }}
          >
            Sift Trip
          </span>
        </Link>

        {/* ── Desktop nav ───────────────────────────────────────────────── */}
        <div className="hidden md:flex items-center gap-6">
          <Link
            to="/"
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            {t("nav.home")}
          </Link>
          <Link
            to="/experiences"
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            {t("nav.explore")}
          </Link>
          {/* Brand Stabilization Legacy Cleanup V1 — the public /planner nav
              link was removed. It promoted the unfinished legacy planner
              (multi-day AI itinerary generator) as a primary launch surface,
              which this marketplace launch doesn't ship. The route itself is
              untouched and still reachable by direct URL — only the public
              promotion of it is gone. Re-add here once Planner is a real,
              launch-ready product again. */}
          <a
            href="#como-funciona"
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            {t("nav.howItWorks")}
          </a>

          {/* Auth section — only rendered when Supabase is configured */}
          {isSupabaseEnabled && !loading && (
            user ? (
              <UserChip />
            ) : (
              <LoginButton compact />
            )
          )}
        </div>

        {/* ── Mobile right side ─────────────────────────────────────────── */}
        <div className="md:hidden flex items-center gap-2">
          {/* Compact auth button on mobile */}
          {isSupabaseEnabled && !loading && !user && (
            <LoginButton compact />
          )}

          {/* Hamburger */}
          <button
            className="p-2 text-muted-foreground hover:text-foreground transition-colors"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={t("nav.hamburgerMenuAria")}
          >
            <svg width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              {mobileOpen ? (
                <>
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </>
              ) : (
                <>
                  <line x1="3" y1="6"  x2="21" y2="6"  />
                  <line x1="3" y1="12" x2="21" y2="12" />
                  <line x1="3" y1="18" x2="21" y2="18" />
                </>
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* ── Mobile menu ───────────────────────────────────────────────────── */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-background border-b border-border/50"
          >
            <div className="px-4 py-4 flex flex-col gap-3">
              <Link
                to="/"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                onClick={() => setMobileOpen(false)}
              >
                {t("nav.home")}
              </Link>
              <Link
                to="/experiences"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                onClick={() => setMobileOpen(false)}
              >
                {t("nav.explore")}
              </Link>
              <a
                href="#como-funciona"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                onClick={() => setMobileOpen(false)}
              >
                {t("nav.howItWorks")}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
